// Comprehensive content & interactive state data for Prophecy ERP Demo Page
export const ERP_ROLES = [
  {
    id: 'all',
    label: 'All-in-One Overview',
    tagline: 'Complete 360° workforce, staffing, contracts & billing operations in one unified cloud.',
    highlightModules: ['ats', 'bench', 'crm', 'timesheets', 'invoicing', 'h1b', 'msa', 'portal']
  },
  {
    id: 'recruiter',
    label: 'Talent & Recruiting',
    tagline: 'End-to-end applicant tracking, automated resume parsing, candidate stages, and interview pipelines.',
    highlightModules: ['ats', 'bench']
  },
  {
    id: 'sales',
    label: 'Bench Sales & Accounts',
    tagline: 'Hotlists, vendor distributions, client requirements, and fast candidate marketing.',
    highlightModules: ['bench', 'crm']
  },
  {
    id: 'operations',
    label: 'Delivery & HR Managers',
    tagline: 'Timesheet tracking, 1-click approvals, leave management, and consultant onboarding.',
    highlightModules: ['crm', 'timesheets']
  },
  {
    id: 'finance',
    label: 'Finance & Invoicing',
    tagline: 'Automated invoice generation from approved billable hours, payment tracking, and ledger accounts.',
    highlightModules: ['timesheets', 'invoicing']
  },
  {
    id: 'legal',
    label: 'Immigration & Contracts',
    tagline: 'USCIS H-1B intake petitions, LCA tracking, Master Services Agreements, and DocuSign e-signatures.',
    highlightModules: ['h1b', 'msa', 'portal']
  }
];

export const ERP_MODULES = [
  {
    id: 'ats',
    title: 'ATS & Talent Acquisition',
    badge: 'Recruitment Cloud',
    icon: 'Briefcase',
    tagline: 'Source, screen, track, and hire top candidates with an intelligent visual pipeline.',
    simpleExplanation: 'Instead of losing candidate resumes across emails and spreadsheets, the ATS gives your recruitment team a single unified dashboard. Track every candidate from the moment they submit their resume until the final offer letter.',
    painPointsSolved: [
      'Eliminates lost candidate resumes and duplicate submissions',
      'Provides clear visual stage pipelines (Applied, Screened, Interview, Placed)',
      'Direct synchronization with public careers portal and applicant tracking'
    ],
    features: [
      'Visual Drag & Drop Candidate Kanban Board',
      'Automated Resume Parsing & Skill Extraction',
      'Interview Scheduling & Feedback Scorecards',
      'Instant Offer Letter Generation & Handoff to Onboarding'
    ],
    metrics: { stat: '65%', label: 'Faster Time-to-Hire' }
  },
  {
    id: 'bench',
    title: 'Bench Sales & Staffing',
    badge: 'Revenue Accelerator',
    icon: 'TrendingUp',
    tagline: 'Market bench consultants, match high-paying client requirements, and close placements faster.',
    simpleExplanation: 'Bench sales managers need to market available talent to vendors and prime clients immediately. This module centralizes your active consultant hotlist, incoming requirements, vendor communications, and rate negotiations.',
    painPointsSolved: [
      'Zero idle consultant time: quick matching of bench profiles to requirements',
      'Automated vendor blast distribution and rate tracking',
      'Centralized submission tracker preventing multiple recruiters submitting the same profile'
    ],
    features: [
      'Live Consultant Hotlist with Real-Time Availability',
      'Client Requirement Parser & Match Score',
      'Vendor Tier Management & Rate Negotiation Log',
      'Submission Status Tracker with Client Interview Milestones'
    ],
    metrics: { stat: '3.4x', label: 'More Submissions / Day' }
  },
  {
    id: 'crm',
    title: 'CRM & Client Onboarding',
    badge: 'Relationship Suite',
    icon: 'Users',
    tagline: 'Manage corporate accounts, client contacts, deal stages, and structured onboarding pipelines.',
    simpleExplanation: 'Once a candidate is selected, onboarding them into a client account requires multiple steps (background checks, badges, drug tests, systems access). The CRM onboarding drawer gives delivery managers an interactive progress checklist.',
    painPointsSolved: [
      'Stops onboarding delays before billable start dates',
      'Keeps client contracts, contacts, and account managers synchronized',
      'Full visual drawer tracking compliance checklists and documentation status'
    ],
    features: [
      '360° Corporate Client & Department Profiles',
      'Interactive Onboarding Detail Drawer with Live Progress Bar',
      'Document Verification Checklists (I-9, W-4, Background Checks)',
      'Deployment & Start-Date Readiness Reports'
    ],
    metrics: { stat: '99.4%', label: 'On-Time Onboarding' }
  },
  {
    id: 'timesheets',
    title: 'Timesheets & Attendance',
    badge: 'Workforce Tracking',
    icon: 'Clock',
    tagline: 'Weekly work hour logging, multi-level manager approvals, and automated leave management.',
    simpleExplanation: 'Consultants and employees submit their weekly billable and non-billable hours online. Managers review, comment, and approve in 1 click, automatically feeding approved hours straight into billing.',
    painPointsSolved: [
      'No more manual Excel timesheets or chasing managers for sign-offs',
      'Automatic validation of regular hours vs. overtime',
      'Direct link between approved hours and customer invoices'
    ],
    features: [
      'Intuitive Weekly Calendar Grid for Hours Logging',
      '1-Click Manager & Supervisor Approval Workflow',
      'Audit Trail with Timestamped Approval Signatures',
      'Integrated Leave Management & Paid Time Off (PTO) Tracker'
    ],
    metrics: { stat: '100%', label: 'Approval Traceability' }
  },
  {
    id: 'invoicing',
    title: 'Automated Billing & Invoicing',
    badge: 'Financial Engine',
    icon: 'CreditCard',
    tagline: 'Generate clean client invoices straight from approved timesheets without manual calculation.',
    simpleExplanation: 'Manual billing is error-prone and causes delayed payments. Prophecy Nexus automatically pulls approved timesheet hours, applies the contracted hourly rate, adds applicable taxes, and generates client-ready PDF invoices.',
    painPointsSolved: [
      'Zero manual calculation errors between timesheet hours and invoice bills',
      'Automated Net 15/30/60 day payment aging and overdue alerts',
      'Accounts receivable reconciliation in real time'
    ],
    features: [
      '1-Click Invoice Generation from Approved Timesheets',
      'Custom Hourly, Fixed, and Milestone Billing Rates',
      'Client Billing Email Dispatch with PDF Attachments',
      'Payment Status Tracking (Draft, Sent, Paid, Overdue)'
    ],
    metrics: { stat: '0%', label: 'Billing Discrepancies' }
  },
  {
    id: 'h1b',
    title: 'H-1B Immigration Portal',
    badge: 'Compliance & Legal',
    icon: 'ShieldCheck',
    tagline: 'Intake petitions, manage USCIS compliance, document checklists, and multi-tenant legal filings.',
    simpleExplanation: 'Immigration compliance is critical for US consulting and staffing. This module handles H-1B intake questionnaires, document collection, LCA postings, and petition tracking for multiple affiliate companies (Prophecy, Cognifyar, Disensystem).',
    painPointsSolved: [
      'Complex USCIS document requirements simplified into clear steppers',
      'Multi-company intake isolation with custom legal branding',
      'Never miss an RFE or expiration date with automated milestone reminders'
    ],
    features: [
      'Multi-Company Intake Forms (Cognifyar, Disensystem, Prophecy)',
      'Document Repository with Expiration & Visa Status Tracking',
      'Public Access File (PAF) & LCA Compliance Log',
      'Submission Detail Drawer with Lawyer Review Notes'
    ],
    metrics: { stat: '100%', label: 'USCIS Audit Ready' }
  },
  {
    id: 'msa',
    title: 'MSA & Contract Automation',
    badge: 'Digital Agreements',
    icon: 'FileText',
    tagline: 'Master Services Agreements, Statements of Work (SOW), and integrated DocuSign electronic signing.',
    simpleExplanation: 'Drafting and signing legal contracts shouldn’t take weeks. Generate standard MSAs and SOWs automatically, send them to clients or vendors, and execute legally binding signatures via DocuSign directly within the ERP.',
    painPointsSolved: [
      'Eliminates legal bottlenecks with standardized agreement templates',
      'No need to switch to third-party tools: DocuSign is embedded directly',
      'Tokenized public signing URLs for counterparties without requiring accounts'
    ],
    features: [
      'Automated Master Services Agreement (MSA) Generator',
      'Native DocuSign API Integration for E-Signatures',
      'Tokenized Public Counterparty Signing Experience',
      'Executed Agreement Vault with Verifiable Audit Trails'
    ],
    metrics: { stat: '48hr', label: 'Turnaround Time' }
  },
  {
    id: 'portal',
    title: 'Supplier & Partner Portal',
    badge: 'External Ecosystem',
    icon: 'Globe',
    tagline: 'Secure, passwordless OTP portal for external vendors, staffing suppliers, and subcontractors.',
    simpleExplanation: 'External vendors and staffing agencies don’t need full ERP accounts. They securely log in via email OTP to view their placed candidates, submit timesheets, and review contract documents in a clean, restricted portal.',
    painPointsSolved: [
      'External vendors cannot access sensitive internal ERP data',
      'Zero password fatigue: fast and secure One-Time Password (OTP) login',
      'Direct self-service document and invoice uploads for partners'
    ],
    features: [
      'Passwordless Email OTP Authentication',
      'Vendor Candidate & Placement Overview',
      'Subcontractor Timesheet Submission & Status',
      'Shared Legal Documents & Agreement Viewer'
    ],
    metrics: { stat: '100%', label: 'Secure Isolation' }
  }
];

export const LIFECYCLE_STEPS = [
  {
    step: '01',
    title: 'Talent Sourcing & Matching',
    desc: 'Candidates are ingested via the Careers portal, ATS, or Bench Sales hotlist. AI matching pairs top candidates to client job openings in seconds.',
    icon: 'UserCheck',
    color: '#38bdf8'
  },
  {
    step: '02',
    title: 'Client Engagement & Legal',
    desc: 'Selected candidates are attached to client accounts in the CRM. Automated MSAs are generated and sent for electronic signature via DocuSign.',
    icon: 'FileSignature',
    color: '#2dd4bf'
  },
  {
    step: '03',
    title: 'Onboarding & Immigration',
    desc: 'The Onboarding Drawer guides candidates through background checks and I-9 forms. If visa sponsorship is required, the H-1B intake launches automatically.',
    icon: 'Shield',
    color: '#3b82f6'
  },
  {
    step: '04',
    title: 'Timesheets & Direct Billing',
    desc: 'Consultants submit weekly hours. Supervisors approve in 1 click. Approved hours automatically convert into formatted client invoices ready for dispatch.',
    icon: 'Receipt',
    color: '#a855f7'
  }
];

export const SAMPLE_CANDIDATES = [
  {
    id: 'c1',
    name: 'Daniel Brooks',
    role: 'Lead Cloud & DevOps Engineer',
    exp: '8 Years',
    rate: '$110/hr',
    stage: 'Interviewing',
    score: '96%',
    skills: ['AWS', 'Kubernetes', 'Terraform', 'CI/CD']
  },
  {
    id: 'c2',
    name: 'Kavya Reddy',
    role: 'Senior Full Stack Java & React Engineer',
    exp: '7 Years',
    rate: '$95/hr',
    stage: 'Screening',
    score: '92%',
    skills: ['Java', 'Spring Boot', 'React', 'PostgreSQL']
  },
  {
    id: 'c3',
    name: 'Nisha Patel',
    role: 'AI / ML Engineer & Data Scientist',
    exp: '6 Years',
    rate: '$105/hr',
    stage: 'Client Offer',
    score: '98%',
    skills: ['Python', 'PyTorch', 'LLMs', 'MLOps']
  }
];

export const SAMPLE_TIMESHEET = {
  consultant: 'Rahul Menon',
  company: 'Prophecy Consulting Inc',
  client: 'Carrier Technologies (US)',
  period: 'Oct 10 - Oct 16, 2026',
  project: 'Cloud Infrastructure Modernization',
  rate: 95,
  days: [
    { day: 'Mon', hours: 8.0 },
    { day: 'Tue', hours: 8.0 },
    { day: 'Wed', hours: 8.0 },
    { day: 'Thu', hours: 8.0 },
    { day: 'Fri', hours: 8.0 }
  ],
  overtime: 10.12,
  totalHours: 50.12
};

export const SAMPLE_ONBOARDING_TASKS = [
  { id: 't1', title: 'Master Services Agreement (MSA) Executed', status: 'completed', date: 'Oct 10, 2026' },
  { id: 't2', title: 'Background Check & Drug Screening', status: 'completed', date: 'Oct 12, 2026' },
  { id: 't3', title: 'Form I-9 & Right-to-Work Verification', status: 'completed', date: 'Oct 13, 2026' },
  { id: 't4', title: 'Client VPN & Laptop Provisioning', status: 'in_progress', date: 'In Review' },
  { id: 't5', title: 'Timesheet & Project Billing Setup', status: 'pending', date: 'Pending Start' }
];

export const FAQ_ITEMS = [
  {
    q: 'Can external visitors explore the ERP without logging in?',
    a: 'Yes! This entire interactive demo page allows prospective clients, partners, and team members to explore every module, understand the end-to-end workflow, and test-drive simulated features with zero login credentials required.'
  },
  {
    q: 'How does data flow between Recruitment, CRM, and Invoicing?',
    a: 'Prophecy Nexus is fully unified. When a candidate is placed in the ATS, they flow into the CRM Onboarding drawer. Once active, their timesheets automatically link to their contracted billing rate, allowing 1-click invoice generation with zero manual data entry.'
  },
  {
    q: 'Does Prophecy Nexus support DocuSign and H-1B compliance?',
    a: 'Yes, natively! The MSA module features direct DocuSign integration for legal electronic signing, and the H-1B portal provides multi-company intake forms (Prophecy, Cognifyar, Disensystem), public access files, and petition tracking.'
  },
  {
    q: 'Can our external vendors and subcontractors submit timesheets?',
    a: 'Yes. External suppliers access the Supplier Portal using passwordless Email OTP authentication. They can submit consultant hours and view contract documents without having access to your internal ERP data.'
  }
];
